import { ApiError, explain } from '@/shared/api/problem'
import type { SurveyBundleDto } from '@/shared/api/types'
import { submitBatch } from './api'
import { uploadClip, type ClipKind, type HeldClip, type UploadableClip } from './AudioRecorder'

/**
 * Durability for a screen that is used where there is no signal.
 *
 * ## Three different things live here, and they fail differently
 *
 * 1. **The instrument bundle**, cached under its `etag` so a reload on a bus
 *    still has questions to ask.
 * 2. **The interview in progress**, so a reload keeps its `idempotencyKey` and
 *    its answers instead of becoming a second respondent.
 * 3. **Finished interviews that could not be sent**, queued and drained with
 *    `submitBatch`.
 * 4. **Clips whose interview HAS been sent but whose upload has not finished**,
 *    indexed separately by `responseId` and swept by every drain. They are not
 *    interviews and are not counted as any: the answers are already stored, and a
 *    depth that added the two together would tell an interviewer they have unsent
 *    interviews when they have none.
 *
 * ## Every localStorage access is wrapped, reads included
 *
 * Not defensiveness for its own sake: in a Safari private window, and in any
 * browser configured to block site data, `window.localStorage` **throws on
 * property access** — `SecurityError`, before `getItem` is ever called. A guard
 * that only wraps writes crashes the whole capture screen at import time on the
 * exact devices that most need it. So the accessor itself is inside the try, and
 * a browser that refuses storage degrades to a session that works but cannot
 * survive a reload — which the UI says out loud rather than pretending.
 *
 * ## What is NOT queued, and why the audio is
 *
 * `localStorage` holds strings and a few megabytes; an interview recording is
 * tens of megabytes of binary. So the answers go to `localStorage` and the
 * **clips go to IndexedDB**, keyed by the same idempotency key. They cannot be
 * uploaded while queued — `reserveMedia` needs a `responseId` and an offline
 * interview does not have one yet — so the order is: drain the batch, read the
 * `responseId` off the per-item result, then upload the clips that were waiting
 * for it. A browser with no IndexedDB keeps the interview and loses the clip,
 * and says so; losing the answers to save the audio would be the wrong trade.
 */

const QUEUE_KEY = 'axis.capture.queue.v1'
const REJECTED_KEY = 'axis.capture.rejected.v1'
const CLIPS_KEY = 'axis.capture.clips.v1'
const BUNDLE_PREFIX = 'axis.capture.bundle.v1.'
const DRAFT_PREFIX = 'axis.capture.draft.v1.'

// ── storage that cannot throw ───────────────────────────────────────────────

/** Set once the first access throws, so the UI can say storage is unavailable. */
let storageBlocked = false

function readRaw(key: string): string | null {
  try {
    // `window.localStorage` itself is the throwing expression in a private
    // window. Touching it inside the try is the whole point of this function.
    return window.localStorage.getItem(key)
  } catch {
    storageBlocked = true
    return null
  }
}

function writeRaw(key: string, value: string): boolean {
  try {
    window.localStorage.setItem(key, value)
    return true
  } catch {
    // Also the quota path: a full origin throws `QuotaExceededError` on write
    // while reads keep working, so this is not necessarily the same failure as
    // a blocked read.
    storageBlocked = true
    return false
  }
}

function removeRaw(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    storageBlocked = true
  }
}

function readJson<T>(key: string, fallback: T): T {
  const raw = readRaw(key)
  if (raw === null) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    // Corrupt is not the same as absent, but for a cache the only useful
    // response to either is the fallback. It is dropped rather than left to
    // fail on every subsequent read.
    removeRaw(key)
    return fallback
  }
}

function writeJson(key: string, value: unknown): boolean {
  try {
    return writeRaw(key, JSON.stringify(value))
  } catch {
    return false
  }
}

export function isStorageBlocked(): boolean {
  return storageBlocked
}

// ── the bundle cache ────────────────────────────────────────────────────────

interface CachedBundle {
  etag: string
  cachedAt: string
  bundle: SurveyBundleDto
}

/**
 * Cache the instrument under its ETag.
 *
 * The etag is stored beside the bundle rather than used as the key: a device
 * wants "the newest instrument I have for this poll", and keying by etag would
 * accumulate one entry per version with nothing able to say which is current.
 */
export function cacheBundle(surveyId: string, bundle: SurveyBundleDto): void {
  writeJson(BUNDLE_PREFIX + surveyId, {
    etag: bundle.etag, cachedAt: new Date().toISOString(), bundle,
  } satisfies CachedBundle)
}

export function cachedBundle(surveyId: string): CachedBundle | null {
  return readJson<CachedBundle | null>(BUNDLE_PREFIX + surveyId, null)
}

// ── the interview in progress ───────────────────────────────────────────────

/**
 * Everything about one interview that must survive a reload.
 *
 * The `idempotencyKey` is the load-bearing field. A device that reloads
 * mid-interview and generates a fresh key turns one respondent into two the
 * moment both submissions land, and nothing downstream can tell them apart —
 * the answers are similar, the timestamps are close, and the interviewer is the
 * same person. So the key is written the instant the interview starts and read
 * back before a new one is ever generated.
 */
export interface CaptureDraft {
  idempotencyKey: string
  startedAt: string | null
  languageCode: string
  optionOrderSeed: number
  consentGiven: boolean | null
  consentAt: string | null
  answers: Record<string, unknown>
  details: Record<string, unknown>
}

const draftKey = (surveyId: string, assignmentId: string | null) =>
  `${DRAFT_PREFIX}${surveyId}.${assignmentId ?? 'none'}`

export function saveDraft(
  surveyId: string, assignmentId: string | null, draft: CaptureDraft,
): void {
  writeJson(draftKey(surveyId, assignmentId), draft)
}

export function loadDraft(surveyId: string, assignmentId: string | null): CaptureDraft | null {
  return readJson<CaptureDraft | null>(draftKey(surveyId, assignmentId), null)
}

export function clearDraft(surveyId: string, assignmentId: string | null): void {
  removeRaw(draftKey(surveyId, assignmentId))
}

// ── the queue ───────────────────────────────────────────────────────────────

export interface QueuedInterview {
  idempotencyKey: string
  surveyId: string
  surveyRef: string | null
  /** The exact `ResponseSubmission` body. Stored verbatim so a drain re-sends it unaltered. */
  body: Record<string, unknown>
  queuedAt: string
  attempts: number
  lastProblem: string | null
  /** How many clips are waiting in IndexedDB for this key. */
  clipCount: number
}

export interface RejectedInterview extends QueuedInterview {
  /** The server's sentence, kept verbatim. A rejected item is never dropped. */
  problem: string
  rejectedAt: string
}

/**
 * A clip whose interview has already reached the server.
 *
 * Tracked apart from `pending` because the two wait on different things and used
 * to be conflated: a clip that failed its PUT after its interview was accepted
 * belonged to no list at all, so no later drain ever looked at it again and the
 * blob sat in IndexedDB for good while the interview kept its AUDIO_MISSING flag.
 * Every drain now sweeps this index whether or not any interview needs sending.
 */
export interface WaitingClips {
  /** The interview the clips came from. Kept for the sentence the UI shows. */
  idempotencyKey: string
  /** Also the IndexedDB key the blobs are stored under. */
  responseId: string
  surveyRef: string | null
  count: number
  queuedAt: string
  attempts: number
  lastProblem: string | null
}

export interface QueueSnapshot {
  pending: QueuedInterview[]
  rejected: RejectedInterview[]
  /** Clips still going up against interviews the server already has. */
  waitingClips: WaitingClips[]
  draining: boolean
  online: boolean
  storageBlocked: boolean
  /** The last drain's outcome, in a sentence. Null before the first drain. */
  lastResult: string | null
}

function initial(): QueueSnapshot {
  return {
    pending: readJson<QueuedInterview[]>(QUEUE_KEY, []),
    rejected: readJson<RejectedInterview[]>(REJECTED_KEY, []),
    waitingClips: readJson<WaitingClips[]>(CLIPS_KEY, []),
    draining: false,
    online: typeof navigator === 'undefined' ? true : navigator.onLine,
    storageBlocked,
    lastResult: null,
  }
}

let snapshot: QueueSnapshot = typeof window === 'undefined'
  ? {
    pending: [], rejected: [], waitingClips: [], draining: false, online: true,
    storageBlocked: false, lastResult: null,
  }
  : initial()

const listeners = new Set<() => void>()

function set(patch: Partial<QueueSnapshot>) {
  snapshot = { ...snapshot, ...patch, storageBlocked }
  if (patch.pending) writeJson(QUEUE_KEY, patch.pending)
  if (patch.rejected) writeJson(REJECTED_KEY, patch.rejected)
  if (patch.waitingClips) writeJson(CLIPS_KEY, patch.waitingClips)
  listeners.forEach((l) => l())
}

export function subscribeQueue(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function getQueueSnapshot(): QueueSnapshot {
  return snapshot
}

export function queueDepth(): number {
  return snapshot.pending.length
}

/**
 * Clips waiting, counted separately from interviews waiting.
 *
 * A recording still going up is not an interview still going up — the answers
 * are already on the server — and adding the two together would tell an
 * interviewer they have unsent interviews when they do not.
 */
export function waitingClipCount(): number {
  return snapshot.waitingClips.reduce((n, w) => n + w.count, 0)
}

/**
 * Take an interview that could not be sent.
 *
 * Called for exactly two reasons: the device is offline, or the POST failed with
 * a **network** error. Never for a 4xx — a refusal is the server having read the
 * submission and said no, and queueing it would retry the same refusal forever
 * while the interviewer believes it is on its way.
 */
export async function queueInterview(
  surveyId: string,
  surveyRef: string | null,
  body: Record<string, unknown>,
  clips: HeldClip[],
  reason: string | null,
): Promise<void> {
  const key = String(body.idempotencyKey ?? '')
  if (!key) return
  const stored = clips.length > 0 ? await stashClips(key, clips) : 0
  const item: QueuedInterview = {
    idempotencyKey: key,
    surveyId,
    surveyRef,
    body,
    queuedAt: new Date().toISOString(),
    attempts: 0,
    lastProblem: reason,
    clipCount: stored,
  }
  // Keyed replace, not append: re-queueing the same interview after a failed
  // drain must not put two copies in the list. They would both be DUPLICATE on
  // the server, but the depth on screen would be a lie.
  const pending = [...snapshot.pending.filter((p) => p.idempotencyKey !== key), item]
  set({ pending })
}

/**
 * How many interviews go in one `POST …/responses/batch`.
 *
 * Mirrors `survey.capture.max-batch`, whose default is 100. A device that spent a
 * full day out of signal — the case this whole module exists for — accumulates
 * more than that, and a single oversized batch is refused whole with one 4xx, so
 * the queue could never drain itself again. Chunked here rather than trusted to
 * stay small.
 */
const MAX_BATCH = 100

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size))
  return out
}

/**
 * Send everything, one batch per poll, at most {@link MAX_BATCH} per request.
 *
 * `BatchResult` is per-item on purpose: one bad row must not lose the queue. A
 * `DUPLICATE` is a **success** — it means an earlier attempt did land and the
 * server is handing back the interview it already has, which is precisely what
 * the idempotency key was for. Treating it as an error would leave the item
 * queued forever and tell the interviewer something went wrong when nothing did.
 *
 * ## Everything is merged against the LIVE snapshot at write time
 *
 * A drain awaits the network for as long as the network takes, and the screen
 * goes on working meanwhile: `submit()` fires a drain after every success, so the
 * next interview to fail is queued WHILE this one is in flight, and
 * `retryRejected` moves an item between the two lists with no await at all.
 * Writing back a list computed from the pre-drain snapshot deleted both. So the
 * drain only ever claims the keys it actually answered for, and everything else
 * in the live snapshot survives untouched.
 */
export async function drainQueue(): Promise<void> {
  if (snapshot.draining) return
  if (snapshot.pending.length === 0 && snapshot.waitingClips.length === 0) return
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  set({ draining: true })

  const bySurvey = new Map<string, QueuedInterview[]>()
  for (const item of snapshot.pending) {
    bySurvey.set(item.surveyId, [...(bySurvey.get(item.surveyId) ?? []), item])
  }

  let sent = 0
  let duplicated = 0
  let refused = 0
  let held = 0
  let clipsUploaded = 0
  const stillPending: QueuedInterview[] = []
  const newlyRejected: RejectedInterview[] = []
  const newlyWaiting: WaitingClips[] = []
  /** The keys this drain took responsibility for — nothing else may be touched. */
  const handled = new Set<string>()

  for (const [surveyId, items] of bySurvey) {
    const chunks = chunk(items, MAX_BATCH)
    for (let c = 0; c < chunks.length; c += 1) {
      const batch = chunks[c]!
      batch.forEach((item) => handled.add(item.idempotencyKey))
      try {
        const result = await submitBatch(surveyId, batch.map((i) => i.body))
        for (const outcome of result.items) {
          // The index is into THIS chunk, which is what was sent.
          const item = batch[outcome.index]
          if (!item) continue
          if (outcome.outcome === 'CREATED' || outcome.outcome === 'DUPLICATE') {
            if (outcome.outcome === 'DUPLICATE') duplicated += 1
            else sent += 1
            if (outcome.responseId) {
              const flushed = await flushClips(outcome.responseId, item)
              clipsUploaded += flushed.uploaded
              if (flushed.waiting) newlyWaiting.push(flushed.waiting)
            } else await dropClips(item.idempotencyKey)
          } else {
            refused += 1
            newlyRejected.push({
              ...item,
              problem: outcome.problem
                ?? 'The server refused this interview without saying why.',
              rejectedAt: new Date().toISOString(),
            })
          }
        }
        // An index the server did not answer for is still ours to keep.
        const answered = new Set(result.items.map((i) => i.index))
        batch.forEach((item, index) => {
          if (!answered.has(index)) { held += 1; stillPending.push(item) }
        })
      } catch (e) {
        // The whole chunk failed — a network drop, or the server refusing the call
        // itself. Either way the items are untouched and stay queued; a 4xx here
        // is recorded against each so the interviewer can see why they are stuck.
        const message = explain(e)
        const fatal = e instanceof ApiError && e.status >= 400 && e.status < 500
        for (const item of batch) {
          held += 1
          stillPending.push({
            ...item,
            attempts: item.attempts + 1,
            lastProblem: fatal
              ? `${message} This will not clear on its own.`
              : message,
          })
        }
        if (!fatal) {
          // The signal went, or the server is down. The remaining chunks for this
          // poll would each spend a request discovering the same thing, so they
          // are kept as they are — not attempted, so not counted as an attempt.
          for (const rest of chunks.slice(c + 1)) {
            for (const item of rest) {
              handled.add(item.idempotencyKey)
              held += 1
              stillPending.push(item)
            }
          }
          break
        }
        // A refusal of the CALL — a poll that closed, a token that expired — says
        // nothing about the next chunk, so the sweep goes on.
      }
    }
  }

  const swept = await sweepWaitingClips(newlyWaiting)

  const refusedKeys = new Set(newlyRejected.map((r) => r.idempotencyKey))
  set({
    // `snapshot` is read HERE, after every await, so an interview queued mid-drain
    // is still in the list this replaces rather than being written out of
    // existence by a list computed before it existed.
    pending: [
      ...snapshot.pending.filter((p) => !handled.has(p.idempotencyKey)),
      ...stillPending,
    ],
    rejected: [
      ...snapshot.rejected.filter((r) => !refusedKeys.has(r.idempotencyKey)),
      ...newlyRejected,
    ],
    waitingClips: [
      ...snapshot.waitingClips.filter((w) => !swept.handled.has(w.responseId)),
      ...swept.remaining,
    ],
    draining: false,
    lastResult: describeDrain(
      sent, duplicated, refused, held, clipsUploaded + swept.uploaded, swept.remaining,
    ),
  })
}

function describeDrain(
  sent: number,
  duplicated: number,
  refused: number,
  held: number,
  clipsUploaded: number,
  clipsWaiting: WaitingClips[],
): string {
  const parts: string[] = []
  if (sent > 0) parts.push(`${sent} sent`)
  // Named as what it is. A duplicate here means the retry worked the FIRST time
  // and the device never heard the answer — a success with a confusing name.
  if (duplicated > 0) {
    parts.push(`${duplicated} already had been sent and the server returned the original`)
  }
  if (refused > 0) parts.push(`${refused} refused`)
  if (held > 0) parts.push(`${held} still waiting`)
  // Counted apart from the interviews, because a recording still going up does
  // not mean an interview has not arrived.
  if (clipsUploaded > 0) parts.push(`${clipsUploaded} recording${plural(clipsUploaded)} uploaded`)
  const stillGoing = clipsWaiting.reduce((n, w) => n + w.count, 0)
  if (stillGoing > 0) parts.push(`${stillGoing} recording${plural(stillGoing)} still going up`)
  return parts.length === 0 ? 'Nothing to send.' : `${parts.join(', ')}.`
}

const plural = (n: number) => (n === 1 ? '' : 's')

/**
 * Push a refused interview back into the queue — the poll went live, say.
 *
 * Safe to press during a drain: it takes the item out of `rejected` and puts it
 * in `pending` in one write, and the drain only claims the keys it answered for,
 * so it cannot write this interview back out of both lists and lose it entirely.
 */
export function retryRejected(idempotencyKey: string): void {
  const item = snapshot.rejected.find((r) => r.idempotencyKey === idempotencyKey)
  if (!item) return
  // Rebuilt field by field rather than spread-and-strip: `problem` and
  // `rejectedAt` must not survive into a pending item, and a spread would carry
  // them silently the next time either interface grows a field.
  const restored: QueuedInterview = {
    idempotencyKey: item.idempotencyKey,
    surveyId: item.surveyId,
    surveyRef: item.surveyRef,
    body: item.body,
    queuedAt: item.queuedAt,
    attempts: 0,
    lastProblem: null,
    clipCount: item.clipCount,
  }
  set({
    rejected: snapshot.rejected.filter((r) => r.idempotencyKey !== idempotencyKey),
    pending: [...snapshot.pending, restored],
  })
}

/**
 * Delete a refused interview for good.
 *
 * Explicit and one at a time, because this is the only path by which an
 * interview leaves this device without reaching the server. Nothing here ever
 * discards an item on its own.
 */
export function discardRejected(idempotencyKey: string): void {
  void dropClips(idempotencyKey)
  set({ rejected: snapshot.rejected.filter((r) => r.idempotencyKey !== idempotencyKey) })
}

// ── the clips, in IndexedDB ─────────────────────────────────────────────────

const DB_NAME = 'axis-capture'
const DB_VERSION = 1
const CLIP_STORE = 'clips'

interface StoredClip {
  kindCode: ClipKind
  questionId: string | null
  blob: Blob
  contentType: string
  sizeBytes: number
  durationSeconds: number
  capturedAt: string
  /**
   * Both null until a reservation succeeds, then both set. Persisted with the
   * blob so a retry after a reload still resumes at the PUT rather than reserving
   * a second media row for the same clip.
   */
  mediaId?: string | null
  uploadUrl?: string | null
}

/** Null rather than a throw: a browser with no IndexedDB keeps the interview. */
function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') { resolve(null); return }
      const req = indexedDB.open(DB_NAME, DB_VERSION)
      req.onupgradeneeded = () => {
        const db = req.result
        if (!db.objectStoreNames.contains(CLIP_STORE)) db.createObjectStore(CLIP_STORE)
      }
      req.onsuccess = () => resolve(req.result)
      req.onerror = () => resolve(null)
      // A private window can leave the open request hanging on `onblocked`
      // forever rather than erroring, and a capture screen must not wait on it.
      req.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

function txDone(tx: IDBTransaction): Promise<boolean> {
  return new Promise((resolve) => {
    tx.oncomplete = () => resolve(true)
    tx.onerror = () => resolve(false)
    tx.onabort = () => resolve(false)
  })
}

/**
 * Returns how many clips actually made it to disk — zero is a real answer.
 *
 * The store key is the interview's idempotency key while it is queued and its
 * `responseId` once one exists, because those are the two identities a clip can
 * be waiting under and only one of them exists at a time.
 */
async function stashClips(storeKey: string, clips: HeldClip[]): Promise<number> {
  return putClips(storeKey, clips.map((c) => ({
    kindCode: c.kindCode,
    questionId: c.questionId,
    blob: c.blob,
    contentType: c.contentType,
    sizeBytes: c.sizeBytes,
    durationSeconds: c.durationSeconds,
    capturedAt: c.capturedAt,
    mediaId: c.mediaId,
    uploadUrl: c.uploadUrl,
  })))
}

async function putClips(storeKey: string, payload: StoredClip[]): Promise<number> {
  const db = await openDb()
  if (!db) return 0
  try {
    const tx = db.transaction(CLIP_STORE, 'readwrite')
    tx.objectStore(CLIP_STORE).put(payload, storeKey)
    const ok = await txDone(tx)
    return ok ? payload.length : 0
  } catch {
    return 0
  } finally {
    db.close()
  }
}

async function takeClips(storeKey: string): Promise<StoredClip[]> {
  const db = await openDb()
  if (!db) return []
  try {
    const tx = db.transaction(CLIP_STORE, 'readonly')
    const req = tx.objectStore(CLIP_STORE).get(storeKey)
    const value = await new Promise<StoredClip[] | undefined>((resolve) => {
      req.onsuccess = () => resolve(req.result as StoredClip[] | undefined)
      req.onerror = () => resolve(undefined)
    })
    return value ?? []
  } catch {
    return []
  } finally {
    db.close()
  }
}

async function dropClips(storeKey: string): Promise<void> {
  const db = await openDb()
  if (!db) return
  try {
    const tx = db.transaction(CLIP_STORE, 'readwrite')
    tx.objectStore(CLIP_STORE).delete(storeKey)
    await txDone(tx)
  } catch {
    // Nothing useful to do. The blob is orphaned, not lost data.
  } finally {
    db.close()
  }
}

const asUploadable = (clip: StoredClip): UploadableClip => ({
  kindCode: clip.kindCode,
  questionId: clip.questionId,
  blob: clip.blob,
  contentType: clip.contentType,
  sizeBytes: clip.sizeBytes,
  durationSeconds: clip.durationSeconds,
  capturedAt: clip.capturedAt,
  mediaId: clip.mediaId ?? null,
  uploadUrl: clip.uploadUrl ?? null,
})

/**
 * The clips a queued interview was carrying, now that its response exists.
 *
 * Whatever fails is RE-KEYED under the `responseId` and handed back as an index
 * entry. Leaving the bytes under the idempotency key was the losing move: the
 * interview had just been counted CREATED and so was not in `stillPending`, so no
 * later drain ever called this function for that key again and the blob stayed in
 * IndexedDB forever, invisible, while the interview kept its AUDIO_MISSING flag.
 *
 * A clip that succeeded is not re-stashed, so a partial failure does not send the
 * stored ones a second time and produce two media rows for one recording.
 */
async function flushClips(
  responseId: string, item: QueuedInterview,
): Promise<{ uploaded: number; waiting: WaitingClips | null }> {
  const clips = await takeClips(item.idempotencyKey)
  if (clips.length === 0) return { uploaded: 0, waiting: null }
  const outcome = await uploadEach(responseId, clips)
  if (outcome.remaining.length === 0) {
    await dropClips(item.idempotencyKey)
    return { uploaded: outcome.uploaded, waiting: null }
  }
  // Written under the new key BEFORE the old one is deleted. The other order
  // loses the bytes outright on a store that accepts the delete and refuses the
  // write — a full origin does exactly that.
  const kept = await putClips(responseId, outcome.remaining)
  if (kept === 0) return { uploaded: outcome.uploaded, waiting: null }
  await dropClips(item.idempotencyKey)
  return {
    uploaded: outcome.uploaded,
    waiting: {
      idempotencyKey: item.idempotencyKey,
      responseId,
      surveyRef: item.surveyRef,
      count: outcome.remaining.length,
      queuedAt: new Date().toISOString(),
      attempts: 1,
      lastProblem: outcome.problem,
    },
  }
}

/** One pass over a response's clips. `refused` is final and its bytes are dropped. */
async function uploadEach(
  responseId: string, clips: StoredClip[],
): Promise<{ uploaded: number; remaining: StoredClip[]; problem: string | null }> {
  let uploaded = 0
  const remaining: StoredClip[] = []
  let problem: string | null = null
  for (const clip of clips) {
    const outcome = await uploadClip(responseId, asUploadable(clip))
    if (outcome.state === 'stored') { uploaded += 1; continue }
    if (outcome.state === 'refused') {
      // The server read the reservation and said no — too large, wrong type, audio
      // not permitted. The same bytes get the same answer, so keeping them would
      // be a retry counter that never goes down.
      problem = outcome.message
      continue
    }
    // The reservation, if there was one, rides along with the blob so the next
    // sweep resumes at the PUT rather than reserving a second media row.
    remaining.push({ ...clip, mediaId: outcome.mediaId, uploadUrl: outcome.uploadUrl })
    problem = outcome.message
  }
  return { uploaded, remaining, problem }
}

/**
 * Every clip waiting on its own, swept on every drain.
 *
 * Independent of whether the interview it came from still needs sending — that
 * interview is already on the server, which is exactly why nothing else was ever
 * going to come back for these.
 */
async function sweepWaitingClips(
  extra: WaitingClips[],
): Promise<{ uploaded: number; remaining: WaitingClips[]; handled: Set<string> }> {
  // `extra` came out of this drain's own `flushClips`, which has just tried every
  // one of those clips. Retrying them again in the same pass would spend the data
  // twice to learn the same thing, so they are carried forward, not swept.
  const fresh = new Set(extra.map((e) => e.responseId))
  const handled = new Set([...snapshot.waitingClips.map((e) => e.responseId), ...fresh])
  const remaining: WaitingClips[] = [...extra]
  let uploaded = 0

  for (const entry of snapshot.waitingClips) {
    if (fresh.has(entry.responseId)) continue
    const clips = await takeClips(entry.responseId)
    // No blobs under that key any more — a cleared origin, a browser with no
    // IndexedDB. The entry is dropped rather than counted forever.
    if (clips.length === 0) continue
    const outcome = await uploadEach(entry.responseId, clips)
    uploaded += outcome.uploaded
    if (outcome.remaining.length === 0) { await dropClips(entry.responseId); continue }
    const kept = await putClips(entry.responseId, outcome.remaining)
    remaining.push({
      ...entry,
      // A write the store refused leaves the original record in place, so the
      // count on screen is the number that is really still on disk.
      count: kept > 0 ? outcome.remaining.length : clips.length,
      attempts: entry.attempts + 1,
      lastProblem: outcome.problem,
    })
  }
  return { uploaded, remaining, handled }
}

/**
 * Hold clips whose interview HAS been created but whose upload has not finished.
 *
 * Called when the interviewer moves on to the next respondent: the recorder is
 * about to be reset, and a clip still in `held` or `failed` state exists nowhere
 * but memory. The summary screen says the upload "will be retried" — this is what
 * makes that sentence true instead of a lie printed above the button that deletes
 * the bytes.
 */
export async function holdClipsForUpload(
  idempotencyKey: string,
  responseId: string,
  surveyRef: string | null,
  clips: HeldClip[],
): Promise<void> {
  if (clips.length === 0) return
  const stored = await stashClips(responseId, clips)
  if (stored === 0) return
  const entry: WaitingClips = {
    idempotencyKey,
    responseId,
    surveyRef,
    count: stored,
    queuedAt: new Date().toISOString(),
    attempts: 0,
    lastProblem: null,
  }
  // Keyed replace against the live list, for the same reason the queue is: a
  // drain may be in flight and must not have its write clobbered by this one.
  set({
    waitingClips: [
      ...snapshot.waitingClips.filter((w) => w.responseId !== responseId),
      entry,
    ],
  })
  void drainQueue()
}

// ── when to drain ───────────────────────────────────────────────────────────

/**
 * On load and on `online`.
 *
 * Not on a timer: a device that regained signal fires `online`, and one that
 * never had it would spend battery discovering that every thirty seconds. The
 * capture screen also drains explicitly after each submission, which covers the
 * case where `online` is true but the first request of the day is what actually
 * proves it.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    set({ online: true })
    void drainQueue()
  })
  window.addEventListener('offline', () => set({ online: false }))
  // Deferred past module evaluation so an import never blocks on a request.
  setTimeout(() => { void drainQueue() }, 0)
}
